// src/componentes/Escala11/Competicao/AdicionarCompeticao/AdicionarCompeticaoForm/index.tsx

import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Alert,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  SelectChangeEvent,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";

// --- Interfaces de Tipagem (inalteradas) ---
interface Draw {
  id: string;
  name: string;
  drawDate: string;
}

interface DrawsApiResponse {
  draws: Array<{ id: string; title: string; drawDate: string }>;
  total: number;
}

interface CompetitionForm {
  title: string;
  description: string;
  scaleType: string;
  participationPrice: number | string;
  startDate: string;
  closeDate: string;
  targetDrawId: string;
  winnersCount: number | string;
}

interface AdicionarCompeticaoFormProps {
  token: string;
  onSuccess: () => void;
  onClose: () => void;
}

const AdicionarCompeticaoForm: React.FC<AdicionarCompeticaoFormProps> = ({
  token,
  onSuccess,
  onClose,
}) => {
  const [formData, setFormData] = useState<CompetitionForm>({
    title: "",
    description: "",
    scaleType: "",
    participationPrice: 0.0,
    startDate: "",
    closeDate: "",
    targetDrawId: "",
    winnersCount: 3,
  });

  const [draws, setDraws] = useState<Draw[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedDraw = draws.find((draw) => draw.id === formData.targetDrawId);
  const drawDateDisplay = selectedDraw
    ? new Date(selectedDraw.drawDate).toLocaleString()
    : "N/A";

  // --- Lógica de busca e mapeamento do Draw (inalterada) ---
  useEffect(() => {
    const fetchAllDraws = async () => {
      setLoading(true);
      setError(null);

      const allDraws: Draw[] = [];
      let currentPage = 1;
      let totalPages = 1;
      const limit = 50;

      try {
        let url = `https://api.sauhde.com.br/draws?active=true&page=${currentPage}&limit=${limit}`;
        let response = await fetch(url, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.message ||
              `Falha ao buscar Draws ativos. Status: ${response.status}`
          );
        }

        let rawData: DrawsApiResponse = await response.json();

        const totalItems = rawData.total || 0;
        totalPages = Math.ceil(totalItems / limit);

        if (Array.isArray(rawData.draws)) {
          const mappedDraws: Draw[] = rawData.draws.map((draw) => ({
            id: draw.id,
            name: draw.title,
            drawDate: draw.drawDate,
          }));
          allDraws.push(...mappedDraws);
        }

        currentPage++;

        while (currentPage <= totalPages) {
          url = `https://api.sauhde.com.br/draws?page=${currentPage}&limit=${limit}&active=true`;

          response = await fetch(url, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(
              errorData.message ||
                `Falha ao buscar Draws na página ${currentPage}. Status: ${response.status}`
            );
          }

          rawData = await response.json();

          if (Array.isArray(rawData.draws)) {
            const mappedDraws: Draw[] = rawData.draws.map((draw) => ({
              id: draw.id,
              name: draw.title,
              drawDate: draw.drawDate,
            }));
            allDraws.push(...mappedDraws);
          }

          currentPage++;
        }

        setDraws(allDraws);

        if (allDraws.length > 0) {
          setFormData((prev) => ({ ...prev, targetDrawId: allDraws[0].id }));
        }
      } catch (err) {
        console.error("Erro ao buscar draws:", err);
        setError(
          `Não foi possível carregar os Draws ativos: ${(err as Error).message}`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchAllDraws();
  }, [token]);

  // --- Handler de Mudança Corrigido para Inputs Numéricos (inalterado) ---
  const handleChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    let finalValue: string | number = value as string;

    if (name === "participationPrice" || name === "winnersCount") {
      if (value === "") {
        finalValue = "";
      } else {
        const parsedValue = parseFloat(value as string);

        if (name === "winnersCount" && !isNaN(parsedValue)) {
          finalValue = parsedValue;
        } else {
          finalValue = value as string;
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name as string]: finalValue,
    }));
  };

  // --- Lógica de Submissão (POST) (inalterada) ---
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    // Validação de Data: CloseDate não pode ser posterior a DrawDate
    if (selectedDraw) {
      const closeDateTime = new Date(formData.closeDate).getTime();
      const drawDateTime = new Date(selectedDraw.drawDate).getTime();

      if (closeDateTime > drawDateTime) {
        setError(
          `A Data de Fechamento da competição (${new Date(
            formData.closeDate
          ).toLocaleString()}) não pode ser posterior à Data do Sorteio (${drawDateDisplay}).`
        );
        setIsSubmitting(false);
        return;
      }
    }

    if (formData.participationPrice === "" || formData.winnersCount === "") {
      setError(
        "O Preço de Participação e o Número de Vencedores são obrigatórios."
      );
      setIsSubmitting(false);
      return;
    }

    const participationPriceNumber =
      typeof formData.participationPrice === "string"
        ? parseFloat(formData.participationPrice)
        : formData.participationPrice;

    const winnersCountNumber =
      typeof formData.winnersCount === "string"
        ? parseInt(formData.winnersCount, 10)
        : formData.winnersCount;

    const body: any = {
      ...formData,
      startDate: new Date(formData.startDate).toISOString(),
      closeDate: new Date(formData.closeDate).toISOString(),
      participationPrice: parseFloat(participationPriceNumber.toFixed(2)),
      winnersCount: winnersCountNumber,
    };

    try {
      const response = await fetch(
        "https://api.sauhde.com.br/admin/scale-competitions",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Erro HTTP: ${response.status} - Falha ao criar competição.`
        );
      }

      alert("Competição criada com sucesso!");
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Erro ao criar competição:", err);
      setError(`Erro ao criar competição: ${(err as Error).message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Renderização ---

  if (loading) return <Typography>Carregando Draws ativos...</Typography>;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {/* Alerta de Erro */}
      {error && <Alert severity="error">{error}</Alert>}

      {/* Container Principal do Formulário - SUBSTITUINDO GRID */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: "16px", // Simula spacing={2}
          "& > div": {
            // Aplica margem para compensar o gap, mas FullWidth não precisa
            // Apenas define a largura base
            width: { xs: "100%" },
          },
        }}
      >
        {/* Título (xs=12) */}
        <Box sx={{ width: "100%" }}>
          <TextField
            fullWidth
            label="Título"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </Box>

        {/* Descrição (xs=12) */}
        <Box sx={{ width: "100%" }}>
          <TextField
            fullWidth
            label="Descrição"
            name="description"
            value={formData.description}
            onChange={handleChange}
            multiline
            rows={3}
            required
          />
        </Box>

        {/* Tipo de Escala (xs=12, sm=6) */}
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <TextField
            fullWidth
            label="Tipo de Escala"
            name="scaleType"
            value={formData.scaleType}
            onChange={handleChange}
            required
            placeholder="Ex: FUTEBOL, Basquete, E-sports"
          />
        </Box>

        {/* Preço de Participação (xs=12, sm=6) */}
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <TextField
            fullWidth
            label="Preço de Participação (R$)"
            name="participationPrice"
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            value={String(formData.participationPrice)}
            onChange={handleChange}
            required
          />
        </Box>

        {/* Draw Alvo (Select da API) (xs=12) */}
        <Box sx={{ width: "100%", mb: 1 }}>
          <FormControl fullWidth required disabled={draws.length === 0}>
            <InputLabel id="draw-id-label">Sorteio Associado</InputLabel>
            <Select
              labelId="draw-id-label"
              label="Draw Alvo (**targetDrawId**)"
              name="targetDrawId"
              value={formData.targetDrawId || ""}
              onChange={handleChange}
            >
              {draws.length === 0 ? (
                <MenuItem value="" disabled>
                  Nenhum Draw ativo encontrado
                </MenuItem>
              ) : (
                draws.map((draw) => (
                  <MenuItem key={draw.id} value={draw.id}>
                    {draw.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          {/* Exibição da drawDate do Draw selecionado */}
          {selectedDraw && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.5, display: "block" }}
            >
              Data do Sorteio (Referência): <strong>{drawDateDisplay}</strong>
            </Typography>
          )}
          {draws.length === 0 && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: "block" }}
            >
              Nenhum Draw ativo para selecionar.
            </Typography>
          )}
        </Box>

        {/* Data Início (xs=12, sm=6) */}
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <TextField
            fullWidth
            label="Data de Início"
            name="startDate"
            type="datetime-local"
            value={formData.startDate.substring(0, 16)}
            onChange={handleChange}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Data Fechamento (CRÍTICA: VALIDAÇÃO CONTRA drawDate) (xs=12, sm=6) */}
        <Box sx={{ width: { xs: "100%", sm: "calc(50% - 8px)" } }}>
          <TextField
            fullWidth
            label="Data de Fechamento"
            name="closeDate"
            type="datetime-local"
            value={formData.closeDate.substring(0, 16)}
            onChange={handleChange}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Contagem de Vencedores (xs=12) */}
        <Box sx={{ width: "100%" }}>
          <TextField
            fullWidth
            label="Número de Vencedores"
            name="winnersCount"
            type="number"
            inputProps={{ min: "1" }}
            value={String(formData.winnersCount)}
            onChange={handleChange}
            required
          />
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          variant="outlined"
          sx={{
            color: "#008870",
            border: "1px solid #008870",
            "&:hover": {
              color: "#005848ff",
              border: "1.5px solid #005848ff",
            },
          }}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || draws.length === 0}
          variant="contained"
          sx={{
            backgroundColor: "#008870",
            "&:hover": {
              backgroundColor: "#005848ff",
            },
          }}
        >
          {isSubmitting ? "Criando..." : "Criar Competição"}
        </Button>
      </Box>
    </Box>
  );
};

export default AdicionarCompeticaoForm;
